import { useEffect, useState } from "react";
import api, { euro, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Layers, Plus, Trash2, Pencil, X } from "lucide-react";

export default function Plans() {
  const [plans, setPlans] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/admin/plans").then(({ data }) => setPlans(data));
  useEffect(() => { load(); }, []);

  const del = async (key) => {
    try { await api.delete(`/admin/plans/${key}`); toast.success("Piano eliminato"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.15em] font-bold text-stone-500">Super-admin</p>
          <h1 className="font-serif text-4xl sm:text-5xl font-light text-stone-900">Piani di abbonamento</h1>
          <p className="text-sm text-stone-500 mt-2">Definisci le tipologie di abbonamento con i costi mensili e annuali.</p>
        </div>
        <button data-testid="plan-new-btn" onClick={() => setEditing({ _new: true, key: "", name: "", price_monthly_eur: "", price_annual_eur: "", max_users: "" })}
          className="rounded-full bg-brand text-white px-5 py-2.5 text-sm font-semibold flex items-center gap-2 hover:bg-brand-hover"><Plus size={16} /> Nuovo piano</button>
      </div>

      <div className="bg-white border border-stone-200 rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-stone-200 text-xs uppercase tracking-[0.12em] text-stone-500">
            <th className="text-left font-bold py-4 px-6">Piano</th>
            <th className="text-left py-4 px-6 font-bold">Chiave</th>
            <th className="text-right py-4 px-6 font-bold">Mensile</th>
            <th className="text-right py-4 px-6 font-bold">Annuale</th>
            <th className="text-right py-4 px-6 font-bold">Max utenti</th>
            <th className="py-4 px-6"></th></tr></thead>
          <tbody>
            {plans.map((p) => (
              <tr key={p.key} data-testid={`plan-row-${p.key}`} className="border-b border-stone-100">
                <td className="py-4 px-6 font-semibold text-stone-800">{p.name}</td>
                <td className="py-4 px-6 text-stone-500">{p.key}</td>
                <td className="py-4 px-6 text-right text-stone-700">{euro(p.price / 100)}<span className="text-xs text-stone-400">/mese</span></td>
                <td className="py-4 px-6 text-right text-stone-700">{euro((p.price_annual ?? p.price * 10) / 100)}<span className="text-xs text-stone-400">/anno</span></td>
                <td className="py-4 px-6 text-right text-stone-600">{p.max_users}</td>
                <td className="py-4 px-6 text-right">
                  <div className="flex items-center justify-end gap-3">
                    <button data-testid={`plan-edit-${p.key}`} onClick={() => setEditing({ key: p.key, name: p.name, price_monthly_eur: p.price / 100, price_annual_eur: (p.price_annual ?? p.price * 10) / 100, max_users: p.max_users })} className="text-stone-400 hover:text-brand"><Pencil size={16} /></button>
                    <button data-testid={`plan-del-${p.key}`} onClick={() => del(p.key)} className="text-stone-400 hover:text-alert"><Trash2 size={16} /></button>
                  </div>
                </td>
              </tr>
            ))}
            {plans.length === 0 && <tr><td colSpan={6} className="py-10 text-center text-stone-400"><Layers size={28} className="mx-auto mb-2" /> Nessun piano definito</td></tr>}
          </tbody>
        </table>
      </div>

      {editing && <PlanDialog plan={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function PlanDialog({ plan, onClose, onSaved }) {
  const [f, setF] = useState({ ...plan });
  const [saving, setSaving] = useState(false);
  const isNew = plan._new;
  const inp = "w-full border border-stone-300 bg-white px-3 py-2 text-sm rounded-sm outline-none focus:ring-2 focus:ring-brand/40";
  const save = async () => {
    if (!f.key || !f.name) { toast.error("Chiave e nome richiesti"); return; }
    const body = { key: f.key, name: f.name, price_monthly_eur: Number(f.price_monthly_eur) || 0, price_annual_eur: Number(f.price_annual_eur) || 0, max_users: Number(f.max_users) || 1 };
    setSaving(true);
    try { await api.put("/admin/plans", body); toast.success("Piano salvato"); onSaved(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-stone-900/40 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div data-testid="plan-dialog" className="bg-white rounded-sm w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-6 border-b border-stone-200">
          <h3 className="font-serif text-2xl text-stone-900">{isNew ? "Nuovo piano" : "Modifica piano"}</h3>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-800"><X size={20} /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs uppercase tracking-[0.15em] font-bold text-stone-500">Chiave</label>
              <input data-testid="plan-key" disabled={!isNew} className={`${inp} mt-1.5 ${!isNew ? "bg-stone-100 text-stone-400" : ""}`} value={f.key} onChange={(e) => setF({ ...f, key: e.target.value })} placeholder="es. base" /></div>
            <div><label className="text-xs uppercase tracking-[0.15em] font-bold text-stone-500">Nome</label>
              <input data-testid="plan-name" className={`${inp} mt-1.5`} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="es. Base" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs uppercase tracking-[0.15em] font-bold text-stone-500">Costo mensile (€)</label>
              <input data-testid="plan-monthly" type="number" min="0" step="0.01" className={`${inp} mt-1.5`} value={f.price_monthly_eur} onChange={(e) => setF({ ...f, price_monthly_eur: e.target.value })} /></div>
            <div><label className="text-xs uppercase tracking-[0.15em] font-bold text-stone-500">Costo annuale (€)</label>
              <input data-testid="plan-annual" type="number" min="0" step="0.01" className={`${inp} mt-1.5`} value={f.price_annual_eur} placeholder={f.price_monthly_eur ? String((Number(f.price_monthly_eur) * 10).toFixed(2)) : "auto (10× mensile)"} onChange={(e) => setF({ ...f, price_annual_eur: e.target.value })} />
              <p className="text-xs text-stone-400 mt-1">Vuoto = sconto automatico 10× mensile (2 mesi gratis)</p></div>
          </div>
          <div><label className="text-xs uppercase tracking-[0.15em] font-bold text-stone-500">Max utenti</label>
            <input data-testid="plan-maxusers" type="number" min="1" className={`${inp} mt-1.5`} value={f.max_users} onChange={(e) => setF({ ...f, max_users: e.target.value })} /></div>
        </div>
        <div className="p-6 border-t border-stone-200 flex justify-end gap-3">
          <button onClick={onClose} className="px-5 py-2.5 text-sm text-stone-600 hover:bg-stone-100">Annulla</button>
          <button data-testid="plan-save" onClick={save} disabled={saving} className="rounded-full bg-brand text-white px-6 py-2.5 text-sm font-semibold hover:bg-brand-hover disabled:opacity-60">{saving ? "Salvataggio…" : "Salva"}</button>
        </div>
      </div>
    </div>
  );
}
